/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-unsafe-optional-chaining */
import React, { useEffect, useRef, useState } from 'react';
import Calendar from '@toast-ui/react-calendar';
import { isMobile } from 'react-device-detect';
import { Button, Container, Box, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { getMonthString, ProjectStatus } from '../../utils/general';
import '@toast-ui/calendar/dist/toastui-calendar.min.css';
import 'tui-date-picker/dist/tui-date-picker.css';
import 'tui-time-picker/dist/tui-time-picker.css';
import CalendarDetailPopup from '../../components/calendar/CalendarDetailPopup';
import useCalendarStore from '../../store/zustand/calendar';
import api from '../../api';
import { weekDays } from '../../helpers/weekDays';
import useAuthStore from '../../store/zustand/auth';
import Loader from '../../components/shared/Loader';

const MARGIN_Y = 20;
const MARGIN_X = 100;

const MobileStyle = {
  margin: '20px',
  width: 'auto'
};

const DesktopStyle = {
  marginX: `${MARGIN_X}px`,
  marginY: `${MARGIN_Y}px`,
  width: 'auto'
};

const CalendarView: React.FC = function CalendarView() {
  const monthArray = getMonthString('short');
  const [displayMonth, setDisplayMonth] = useState<string>();
  const [displayYear, setDisplayYear] = useState<number>();
  const [projectFilter, setProjectFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('customer');
  const [taskFilter, setTaskFilter] = useState('task');
  const [typeFilter, setTypeFilter] = useState('type');
  const [priorityFilter, setPriorityFilter] = useState('priority');
  const [servicesFilter, setServicesFilter] = useState('services');
  const [openDetailPopup, setOpenDetailPopup] = useState(false);
  const [getCalendarEvents, setGetCalendarEvents] = useState(false);
  const [detailPopUpData, setDetailPopUpData] = useState<any>();
  const [calendarEventsList, setCalendarEventsList] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any>();
  const [calendarEventsProjectsList, setCalendarEventsProjectsList] = useState<
    any[]
  >([]);
  const {
    selectedDate,
    setSelectedDate,
    selectedView,
    setSelectedView,
    setVisibleMonth
  } = useCalendarStore((state) => state);

  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const calendar = useRef<Calendar>(null);
  const calendarContainer = useRef<HTMLElement>(null);
  const pageContainer = useRef<HTMLDivElement>(null);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const { palette, spacing } = useTheme();
  const [selectedMonth, setSelectedMonth] = useState('');
  const userState = useAuthStore((state) => state);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const theme = {
    common: {
      dayName: {
        color: palette.grey[500]
      },
      today: {
        color: 'red'
      }
    }
  };

  const template = {
    allday(event: any) {
      return (
        <span
          style={{
            textDecoration:
              event?.raw?.status === ProjectStatus.COMPLETED
                ? 'line-through'
                : 'none',
            textDecorationColor: palette.mainText.main,
            textDecorationThickness: 2
          }}
        >
          {event?.title}
        </span>
      );
    },
    monthDayName(model: any) {
      return (
        <span
          style={{
            color: palette.grey[500],
            fontSize: spacing(1.5),
            fontWeight: 700
          }}
        >
          {weekDays[model.day].name}
        </span>
      );
    },
    monthGridHeader(model: any) {
      const date = parseInt(model.date.split('-')[2], 10);
      return (
        <span
          style={{
            color: model.isToday ? palette.alert?.[300] : palette.primary[500],
            fontSize: spacing(2),
            fontWeight: 700
          }}
        >
          {date}
        </span>
      );
    },
    monthGridHeaderExceed(hiddenEvents: any) {
      return (
        <span style={{ color: palette.informative[500] }}>
          +{hiddenEvents} more
        </span>
      );
    },
    monthMoreTitleDate(moreTitle: any) {
      const { date } = moreTitle;
      return <span>{date}</span>;
    },
    time(event: any) {
      return (
        <div
          style={{
            backgroundColor: event.backgroundColor,
            color: event.color,
            fontSize: spacing(2),
            fontWeight: 700,
            left: 3,
            margin: '0px 5px 0px 5px',
            overflow: 'hidden',
            padding: '5px 5px 0px 4px',
            position: 'absolute',
            width: '-webkit-fill-available',
            zIndex: 10
          }}
        >
          {event.title}
        </div>
      );
    },
    weekDayName(model: any) {
      return `
      <span style="font-size: 16px; font-weight: 700; color: ${
        palette.primary[400]
      }">${model.date}</span>
      &nbsp;
      <span style="font-weight: 700; font-size: 12px; color: ${
        palette.grey[400]
      }">${weekDays[model.day].name}</span>
      `;
    }
  };

  const {
    data: calendarEventsData,
    error: getCalendarEventsError,
    mutate
  } = useSWR(
    getCalendarEvents && selectedMonth
      ? {
          day: `${displayYear}-${selectedMonth}-01`,
          palette,
          timeFormat: userState?.timeFormat,
          url: 'api/CalendarEvents'
        }
      : null,
    api.CalendarApi.calendarGetProjectsCalendarEvents
  );

  //= ================= FUNCTIONS =============================

  const updateDisplayDate = () => {
    const calendarInstance = calendar.current?.getInstance();
    const date = calendarInstance?.getDate();
    const month = date?.getMonth() ?? 0;
    const year = date?.getFullYear() ?? 0;
    const cero = month < 9 ? '0' : '';
    setDisplayMonth(monthArray[month]);
    setSelectedMonth(`${cero}${String(Number(month) + 1)}`);
    setDisplayYear(year);
    setGetCalendarEvents(true);
    setIsLoading(true);
    if (date) setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  //= ================ USEEFFECT ========================

  useEffect(() => {
    if (calendarEventsData && !getCalendarEventsError) {
      setSelectedMonth('');
      setProjectsList(calendarEventsData.projects);

      const eventsInLocalTimezone = calendarEventsData.events.map(
        (item: any) => {
          const auxStart = new Date(`${item.start}`);
          auxStart.setTime(
            auxStart.getTime() + calendarEventsData.timezone * 60000
          );
          const auxEnd = new Date(`${item.start}`);
          auxEnd.setTime(
            auxEnd.getTime() + calendarEventsData.timezone * 60000
          );

          return {
            ...item,
            end: dayjs(auxEnd).format('MM/DD/YYYY,  h:mm:ss A'),
            start: dayjs(auxStart).format('MM/DD/YYYY,  h:mm:ss A')
          };
        }
      );
      setCalendarEventsList(eventsInLocalTimezone);
      setCalendarEvents(calendarEventsData);
      setCalendarEventsProjectsList(calendarEventsData.eventsProjects);
      setIsLoading(false);
      setGetCalendarEvents(false);
    }
  }, [calendarEventsData]);

  useEffect(() => {
    const calendarInstance = calendar.current?.getInstance();
    calendarInstance?.setDate(moment().toDate());
    setSelectedDate(moment().toDate());
    updateDisplayDate();
    setIsLoading(true);
    setGetCalendarEvents(true);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const calendarInstance = calendar.current?.getInstance();
      calendarInstance?.setDate(selectedDate);
    }
  }, [selectedDate]);

  const getClickPosition = (e: MouseEvent) => {
    setClickPosition({
      x: e.clientX,
      y: e.clientY
    });
  };

  return (
    <Container
      id="calendar-page-container"
      ref={pageContainer}
      sx={isMobile ? MobileStyle : DesktopStyle}
    >
      {/* ── Calendar header ── */}
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        key="calendar-view-header"
        sx={{ mb: 2 }}
      >
        {/* Left: nav + today */}
        <Box alignItems="center" display="flex" gap={1}>
          {/* Prev / month label / Next */}
          <Box
            alignItems="center"
            display="flex"
            sx={{
              backgroundColor: 'white',
              border: '1px solid #E2E4EA',
              borderRadius: '8px',
              gap: 0,
              overflow: 'hidden'
            }}
          >
            <Button
              onClick={() => {
                const calendarInstance = calendar.current?.getInstance();
                calendarInstance?.prev();
                updateDisplayDate();
              }}
              sx={{
                '&:hover': { backgroundColor: '#F5F6F8' },
                borderRadius: 0,
                color: '#005DA2',
                minWidth: 0,
                p: '5px 10px'
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: 13 }} />
            </Button>
            <Typography
              sx={{
                color: '#0f1c2e',
                fontSize: '13px',
                fontWeight: 700,
                px: '10px',
                userSelect: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              {displayMonth} {displayYear}
            </Typography>
            <Button
              onClick={() => {
                const calendarInstance = calendar.current?.getInstance();
                calendarInstance?.next();
                updateDisplayDate();
              }}
              sx={{
                '&:hover': { backgroundColor: '#F5F6F8' },
                borderRadius: 0,
                color: '#005DA2',
                minWidth: 0,
                p: '5px 10px'
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
            </Button>
          </Box>

          {/* Today */}
          <Button
            onClick={() => {
              const calendarInstance = calendar.current?.getInstance();
              calendarInstance?.setDate(moment().toDate());
              updateDisplayDate();
            }}
            sx={{
              '&:hover': { backgroundColor: '#F5F6F8', borderColor: '#C8CBD4' },
              backgroundColor: 'white',
              border: '1px solid #E2E4EA',
              borderRadius: '8px',
              color: '#7D8090',
              fontSize: '11px',
              fontWeight: 600,
              px: '14px',
              py: '6px',
              textTransform: 'none'
            }}
          >
            Today
          </Button>

          {isLoading && <Loader customColor="primary" customSize={24} />}
        </Box>

        {/* Right: Month | Week | Day toggle */}
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #E2E4EA',
            borderRadius: '8px',
            display: 'flex',
            overflow: 'hidden'
          }}
        >
          {(['month', 'week', 'day'] as const).map((v) => (
            <Button
              key={v}
              onClick={() => setSelectedView(v)}
              sx={{
                '&:hover': {
                  backgroundColor: selectedView === v ? '#004d8a' : '#F5F6F8'
                },
                backgroundColor: selectedView === v ? '#005DA2' : 'transparent',
                borderRadius: 0,
                color: selectedView === v ? 'white' : '#7D8090',
                fontSize: '11px',
                fontWeight: 600,
                minWidth: 0,
                px: '16px',
                py: '6px',
                textTransform: 'capitalize',
                transition: 'all 0.15s'
              }}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </Button>
          ))}
        </Box>
      </Box>

      {calendarEventsList && (
        <Box
          id="calendar-container"
          key="calendar-container-box"
          ref={calendarContainer}
        >
          <Calendar
            isReadOnly
            calendars={projectsList}
            events={
              selectedView === 'month'
                ? calendarEventsProjectsList
                : calendarEventsList
            }
            height="800px"
            onClickEvent={(e) => {
              getClickPosition(e.nativeEvent);
              setDetailPopUpData(e.event);
              if (e.event?.id === detailPopUpData?.id) {
                setOpenDetailPopup(!openDetailPopup);
              } else {
                setOpenDetailPopup(true);
              }
            }}
            ref={calendar}
            template={template}
            theme={theme}
            usageStatistics={false}
            view={selectedView}
            week={{
              taskView: false
            }}
          />
          {openDetailPopup && (
            <CalendarDetailPopup
              calendarEventsData={calendarEvents}
              detailPopUpData={detailPopUpData}
              isOpen={openDetailPopup}
              mutate={() => mutate(undefined, true)}
              popUpPosition={clickPosition}
              selectedView={selectedView}
              setOpenDetailPopup={setOpenDetailPopup}
            />
          )}
        </Box>
      )}
    </Container>
  );
};

export default CalendarView;
